import {
    Box,
    FormControlLabel,
    Grid,
    Switch,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
    useTheme
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {observer} from 'mobx-react';
import {useNavigate, useParams} from "react-router-dom";
import * as React from "react";
import CurrentAccountFeature from "./CurrentAccountFeature";
import {useApplication} from "../../ApplicationProvider";
import {useNavigatorFeature} from "../../useFeature";
import LayoutContentToolbar from "../../components/layout/LayoutContentToolbar";
import FilledButton from "../../components/buttons/FilledButton";
import {CURRENT_ACCOUNTS_PATH} from "../../routes";
import LayoutContentWrapper from "../../components/layout/LayoutContentWrapper";
import FormSection from "../../forms/FormSection";
import BalanceCard from "../../components/balanceCard/BalanceCard";
import TabsPanel from "../../components/tabsPanel/TabsPanel";
import InvoicesPanel from "./InvoicesPanel";
import CreditDocumentsPanel from "./CreditDocumentsPanel";
import JudicialStageAdvanceModal from "./JudicialStageAdvanceModal";
import PrimaryRecordPanel from "./PrimaryRecordPanel";
import PrimaryRecordPanelHeader from "./PrimaryRecordPanelHeader";
import AgingBadge from "./AgingBadge";
import StatusBadge from "../../components/statusBadge/StatusBadge";
import PaymentReminderButton from "./payment_reminder/PaymentReminderButton";
import PaymentReminderModal from "./payment_reminder/PaymentReminderModal";


function CurrentAccountScreen() {
    const application = useApplication();
    const navigator = useNavigate();
    const theme = useTheme();
    const {id} = useParams();
    const feature = useNavigatorFeature(() => new CurrentAccountFeature(application, navigator, id), navigator, [id])

    const isFeatureReady = (feature) => {
        return feature && feature.loaded
    }

    const titleWithHighRiskIcon = () => {
        return (
            <Box sx={{display: 'inline-flex', alignItems: 'center', gap: 1}}>
                {feature.customersBusinessName()}
                {feature.isHighRisk() && (
                    <Tooltip title="Alto riesgo: se bloquea ante cualquier mora, sin posibilidad de ampliar el crédito">
                        <WarningAmberIcon sx={{color: 'error.main'}}/>
                    </Tooltip>
                )}
            </Box>
        );
    }

    const customerStatusLine = () => {
        const channel = feature.channel();
        const paymentTerm = feature.paymentTerm();
        const worstTier = feature.worstTier();
        const currentJudicialStageLabel = feature.currentJudicialStageLabel();
        const hasAnyCustomerStatus = channel || paymentTerm || worstTier || currentJudicialStageLabel;
        const showsWorstTierBadge = feature.showsWorstTierBadge();
        return hasAnyCustomerStatus && (
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1, marginTop: 0.5}}>
                {channel && <Typography sx={{color: 'text.secondary', fontSize: '0.9rem'}}>{channel}</Typography>}
                {paymentTerm && <StatusBadge color={theme.palette.text.secondary} label={paymentTerm}/>}
                {showsWorstTierBadge && <AgingBadge tierCode={worstTier.code} label={worstTier.label} theme={theme}/>}
                {feature.hasActiveOrder() && <StatusBadge color={theme.palette.primary.main} label="Pedido activo"/>}
                {currentJudicialStageLabel &&
                    <AgingBadge tierCode={feature.currentJudicialStageIdentifier()} label={currentJudicialStageLabel} theme={theme}/>}
            </Box>
        );
    }

    const customersHeaderSubtitle = () => {
        return (
            <Box>
                <Typography sx={{color: 'text.secondary', fontSize: '0.9rem'}}>{feature.customersFantasyName()}</Typography>
                {customerStatusLine()}
            </Box>
        );
    }

    const documentsTabs = () => {
        const invoiceRows = feature.invoiceRows();
        const creditNoteRows = feature.creditNoteRows();
        const receiptRows = feature.receiptRows();
        return [
            {
                label: () => `Facturas y Notas de Débito ${invoiceRows.length}`,
                panel: () => <InvoicesPanel rows={invoiceRows} columns={feature.invoiceColumns()}
                                            description="Desplegá una factura o nota de débito para ver qué notas de crédito y recibos se imputaron contra ella."/>,
            },
            {
                label: () => `Notas de Crédito ${creditNoteRows.length}`,
                panel: () => <CreditDocumentsPanel rows={creditNoteRows}
                                                   description="Desplegá una nota de crédito para ver a qué factura(s) se imputó."
                                                   emptyLabel="No hay notas de crédito para mostrar"/>,
            },
            {
                label: () => `Recibos de Pago ${receiptRows.length}`,
                panel: () => <CreditDocumentsPanel rows={receiptRows}
                                                   description="Un mismo recibo o certificado de retención puede cancelar total o parcialmente varias facturas. Desplegalo para ver la imputación."
                                                   emptyLabel="No hay recibos de pago ni retenciones para mostrar"/>,
            },
        ];
    }

    const currentAccountViewSelector = () => {
        return (
            <ToggleButtonGroup value={feature.selectedView} exclusive size="small"
                               onChange={(_event, view) => view != null && feature.selectView(view)}
                               sx={{marginBottom: 2}}>
                <ToggleButton value={'applied'} sx={{textTransform: 'none', px: 2}}>
                    Cuenta corriente aplicada
                </ToggleButton>
                <ToggleButton value={'primaryRecord'} sx={{textTransform: 'none', px: 2}}>
                    Documento por documento
                </ToggleButton>
            </ToggleButtonGroup>
        );
    }

    const currentAccountContent = () => {
        return isFeatureReady(feature) && (
            <div>
                <LayoutContentToolbar
                    title={titleWithHighRiskIcon()}
                    subtitle={customersHeaderSubtitle()}
                    breadcrumbs={[
                        {label: 'Cuentas Corrientes', path: CURRENT_ACCOUNTS_PATH},
                        {label: feature.customersBusinessName()},
                    ]}
                >
                    {feature.showsJudicialStageButton() &&
                        <FilledButton
                            disabled={feature.judicialStageButtonIsDisabled()}
                            onClick={() => feature.openJudicialStageModal()}
                        >
                            {feature.judicialStageButtonLabel()}
                        </FilledButton>}
                    {feature.hasHighRiskClassificationPermission() &&
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={feature.isHighRisk()}
                                    onChange={(event) => feature.editRiskClassification(event.target.checked)}
                                />
                            }
                            label="Alto riesgo"
                        />}
                    {feature.hasPaymentReminderPermission() &&
                        <PaymentReminderButton
                            hasOverdueInvoices={feature.paymentReminder.hasOverdueInvoices}
                            disabled={!feature.hasCollectionsEmail}
                            onClick={() => feature.openPaymentReminder()}/>}
                </LayoutContentToolbar>
                <JudicialStageAdvanceModal feature={feature}/>
                <PaymentReminderModal feature={feature.paymentReminderFeature}/>
                <Grid container spacing={2} sx={{marginBottom: '1.5rem'}}>
                    <Grid item xs={12} sm={6} md={3}>
                        <BalanceCard title="Saldo Vencido" amount={feature.overdueBalance()}/>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <BalanceCard title="Saldo A Vencer" amount={feature.outstandingBalance()}/>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <BalanceCard title="Saldo Sin Asignar" amount={feature.unassignedBalance()}/>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <BalanceCard title="Saldo Total" amount={feature.totalBalance()}/>
                    </Grid>
                </Grid>
                {currentAccountViewSelector()}
                {!feature.isPrimaryRecordViewSelected() && (
                    <FormSection title="Comprobantes">
                        <TabsPanel tabs={documentsTabs()}/>
                    </FormSection>
                )}
                {feature.isPrimaryRecordViewSelected() && (
                    <FormSection header={<PrimaryRecordPanelHeader isExporting={feature.isExportingPrimaryRecord} onExport={() => feature.exportPrimaryRecord()}/>}>
                        <PrimaryRecordPanel rows={feature.primaryRecordRows()}/>
                    </FormSection>
                )}
            </div>
        )
    }
    return (
        <LayoutContentWrapper isLoading={!isFeatureReady(feature)} renderContent={() => currentAccountContent()}/>
    );
}

export default observer(CurrentAccountScreen);
